import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Mail, CheckCircle2, ArrowRight, Bell, FileText, Wind } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * NewsletterCapture — one component, three purposeful variants.
 *
 * variant="inline"  — big glass card between homepage sections
 * variant="footer"  — compact single-line form for the footer
 * variant="storm"   — themed "storm alerts" CTA card
 *
 * All three POST to /api/newsletter/subscribe with different `interests`
 * so the admin can segment their monthly send accordingly.
 */
export default function NewsletterCapture({
  variant = "inline",
  defaultMarket = "NYC",
  source,
}) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [market, setMarket] = useState(defaultMarket);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const cfg = variantConfig(variant);

  async function submit(e) {
    e?.preventDefault?.();
    if (!email || !/^[^@]+@[^@]+\.[^@]+$/.test(email)) {
      toast.error("Please enter a valid email");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/newsletter/subscribe`, {
        email,
        name: name || undefined,
        market,
        interests: cfg.interests,
        source: source || cfg.defaultSource,
      });
      setDone(true);
      toast.success(cfg.successToast);
    } catch (err) {
      toast.error("Subscription failed — please try again.");
    } finally {
      setSending(false);
    }
  }

  /* ---------- FOOTER VARIANT ---------- */
  if (variant === "footer") {
    return (
      <div data-testid="newsletter-footer">
        <div className="font-mono text-[10px] uppercase tracking-[0.36em] mb-3" style={{ color: "var(--gold-2)" }}>
          Monthly Portfolio Report
        </div>
        <p className="font-body text-sm mb-4" style={{ color: "var(--text-2)", lineHeight: 1.55 }}>
          One email a month. Seasonal maintenance tips, before/after case files, storm-response advisories. Unsubscribe with one click.
        </p>
        {done ? (
          <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.36em]" style={{ color: "#4ade80" }}>
            <CheckCircle2 size={13} /> Subscribed. Look for us in your inbox.
          </div>
        ) : (
          <form onSubmit={submit} className="flex gap-2">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@company.com"
              className="flex-1 px-4 py-3 font-body text-sm"
              style={{
                background: "rgba(255,255,255,0.04)",
                border: "1px solid rgba(255,255,255,0.08)",
                borderRadius: "12px",
                color: "var(--text)",
              }}
              data-testid="newsletter-footer-email"
              required
            />
            <button
              type="submit"
              disabled={sending}
              className="px-5 py-3 font-mono text-[10px] uppercase tracking-[0.32em] whitespace-nowrap transition-all hover:brightness-110"
              style={{
                background: "var(--gold)",
                color: "#0a0a0d",
                borderRadius: "12px",
                opacity: sending ? 0.55 : 1,
              }}
              data-testid="newsletter-footer-submit"
            >
              {sending ? "…" : "Subscribe"}
            </button>
          </form>
        )}
      </div>
    );
  }

  /* ---------- INLINE / STORM VARIANTS ---------- */
  return (
    <motion.section
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.3 }}
      transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
      className="py-16 md:py-24"
      style={{ background: cfg.sectionBg }}
      data-testid={`newsletter-${variant}`}
    >
      <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-14">
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          {/* Left — pitch */}
          <div className="lg:col-span-7">
            <div className="overline mb-4">{cfg.overline}</div>
            <h2 className="font-display" style={{ fontSize: "clamp(2.4rem, 4.6vw, 4rem)", letterSpacing: "-0.03em", lineHeight: 1.02 }}>
              {cfg.title}{" "}
              <span className="font-display-italic" style={{ color: "var(--gold-2)" }}>{cfg.italic}</span>
            </h2>
            <p className="mt-6 font-body max-w-xl" style={{ color: "var(--text-2)", fontSize: 17, lineHeight: 1.65 }}>
              {cfg.body}
            </p>
            <ul className="mt-8 space-y-3">
              {cfg.bullets.map((b, i) => (
                <li key={i} className="flex items-start gap-3 font-body" style={{ color: "var(--text-2)" }}>
                  <b.Icon size={14} style={{ color: "var(--gold)", marginTop: 4 }} />
                  <span>{b.text}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Right — form card */}
          <div className="lg:col-span-5">
            <div
              className="glass-card glass-reflect p-8 md:p-10 relative overflow-hidden"
              style={{
                borderColor: cfg.borderColor,
                background: cfg.formBg,
              }}
            >
              {done ? (
                <div className="text-center py-6">
                  <div className="w-14 h-14 mx-auto flex items-center justify-center mb-5" style={{
                    borderRadius: "999px",
                    background: "rgba(74,222,128,0.14)",
                    border: "1px solid rgba(74,222,128,0.4)",
                  }}>
                    <CheckCircle2 size={20} style={{ color: "#4ade80" }} strokeWidth={1.5} />
                  </div>
                  <h3 className="font-display text-3xl" style={{ letterSpacing: "-0.03em" }}>
                    You're{" "}
                    <span className="font-display-italic" style={{ color: "var(--gold-2)" }}>on the list.</span>
                  </h3>
                  <p className="mt-3 font-body" style={{ color: "var(--text-2)" }}>{cfg.successBody}</p>
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-4">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 flex items-center justify-center" style={{
                      borderRadius: "999px",
                      background: cfg.iconBg,
                      border: `1px solid ${cfg.borderColor}`,
                    }}>
                      <cfg.HeaderIcon size={16} style={{ color: "var(--gold)" }} strokeWidth={1.5} />
                    </div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.32em]" style={{ color: "var(--gold-2)" }}>
                      {cfg.formHeader}
                    </div>
                  </div>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Full name (optional)"
                    className="w-full px-4 py-3.5 font-body"
                    style={{
                      background: "rgba(255,255,255,0.03)",
                      border: "1px solid rgba(255,255,255,0.08)",
                      borderRadius: "12px",
                      color: "var(--text)",
                    }}
                    data-testid={`newsletter-${variant}-name`}
                  />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Work email"
                    required
                    className="w-full px-4 py-3.5 font-body"
                    style={{
                      background: "rgba(255,255,255,0.03)",
                      border: "1px solid rgba(255,255,255,0.08)",
                      borderRadius: "12px",
                      color: "var(--text)",
                    }}
                    data-testid={`newsletter-${variant}-email`}
                  />
                  <div className="flex gap-2">
                    {["NYC", "Charlotte, NC"].map((m) => (
                      <button
                        key={m}
                        type="button"
                        onClick={() => setMarket(m)}
                        className="flex-1 px-3 py-2.5 font-mono text-[10px] uppercase tracking-[0.28em] transition-all"
                        style={{
                          background: market === m ? "rgba(198,168,124,0.14)" : "rgba(255,255,255,0.03)",
                          border: market === m ? "1px solid var(--gold)" : "1px solid rgba(255,255,255,0.08)",
                          borderRadius: "12px",
                          color: market === m ? "var(--gold-2)" : "var(--text-2)",
                        }}
                        data-testid={`newsletter-${variant}-market-${m.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                      >
                        {m}
                      </button>
                    ))}
                  </div>
                  <button
                    type="submit"
                    disabled={sending}
                    className="btn-glow w-full justify-center mt-2"
                    style={{ opacity: sending ? 0.55 : 1 }}
                    data-testid={`newsletter-${variant}-submit`}
                  >
                    {sending ? "Subscribing…" : cfg.cta} <ArrowRight size={13} />
                  </button>
                  <div className="text-center font-mono text-[9px] uppercase tracking-[0.32em]" style={{ color: "var(--text-3)" }}>
                    One-click unsubscribe · No sharing
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </motion.section>
  );
}

/* ---------- variant configuration ---------- */
function variantConfig(variant) {
  if (variant === "storm") {
    return {
      overline: "Storm-response alerts",
      title: "When the weather hits,",
      italic: "get the alert before you get the calls.",
      body: "When a category-1 wind event, extreme cold, or heavy salt-slurry storm is forecast in your market, we email you 12–24 hours ahead with a pre-emptive service option. You choose whether to greenlight; we handle the crew.",
      bullets: [
        { Icon: Bell, text: "Advance forecast alerts for named events" },
        { Icon: Wind, text: "One-click pre-emptive scheduling — no meetings" },
        { Icon: CheckCircle2, text: "Storm-window re-cleans always included" },
      ],
      interests: ["storm-alerts", "monthly"],
      defaultSource: "storm-cta",
      formHeader: "Storm alerts + monthly report",
      cta: "Enroll in Storm Alerts",
      successToast: "You're on the storm-alert list.",
      successBody: "We'll email you 12–24 hours before any named weather event hits your market.",
      HeaderIcon: Wind,
      sectionBg: "var(--bg)",
      formBg: "linear-gradient(160deg, rgba(28,20,20,0.75), rgba(10,10,13,0.85))",
      borderColor: "rgba(220,120,80,0.35)",
      iconBg: "rgba(220,120,80,0.12)",
    };
  }
  // inline (default)
  return {
    overline: "Monthly Portfolio Report",
    title: "One email a month.",
    italic: "Zero fluff.",
    body: "The seasonal maintenance calendar for your property type, a real before/after case file, and the local weather advisory. Written for facility directors, not marketers.",
    bullets: [
      { Icon: FileText, text: "Seasonal maintenance calendar (customized by market)" },
      { Icon: FileText, text: "One field case file — real numbers, real photos" },
      { Icon: Bell, text: "Local weather advisory for the coming month" },
    ],
    interests: ["monthly"],
    defaultSource: "home-inline",
    formHeader: "Subscribe to the report",
    cta: "Get the Monthly Report",
    successToast: "Subscribed. Look for us in your inbox.",
    successBody: "You'll get the next issue when it goes out. Unsubscribe anytime — one click, no drama.",
    HeaderIcon: Mail,
    sectionBg: "var(--bg-2)",
    formBg: "linear-gradient(160deg, rgba(20,20,24,0.75), rgba(10,10,13,0.9))",
    borderColor: "var(--line-gold)",
    iconBg: "rgba(198,168,124,0.08)",
  };
}
